// @ts-strict-ignore
import _ from 'lodash';
import HttpCodes from 'http-status-codes';
import type { ThresholdMetricInputV1 } from '@/sdk/model/ThresholdMetricInputV1';
import { sqMetricsApi } from '@/sdk/api/MetricsApi';
import { findItemIn, getAllItems, getTrendItemScopedTo, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { DISPLAY_MODE } from '@/main/app.constants';
import type { PropertyOutputV1 } from '@/sdk/model/PropertyOutputV1';
import type { ScalarPropertyV1 } from '@/sdk/model/ScalarPropertyV1';
import { sqFormulasApi } from '@/sdk/api/FormulasApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { errorToast } from '@/utilities/toast.utilities';
import { PUSH_IGNORE } from '@/core/flux.service';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { formatApiError } from '@/utilities/utilities';
import type { FormulaCreator } from '@/utilities/calculationRunner.utilities';
import {
  createCalculatedItem,
  createCondition,
  createScalar,
  createSignal,
  createStatisticsSummary,
  updateFormulaItem,
} from '@/utilities/calculationRunner.utilities';
import { closeInvestigationTool, loadToolForEdit } from '@/toolSelection/investigate.actions';
import { setDisplayMode } from '@/toolSelection/investigateHelpers.actions';
import {
  addTrendItem,
  cancelPreviewCapsules,
  cancelPreviewSeries,
  catchItemDataFailure,
  fetchItemAndDependents,
  removePreviewCapsules,
  removePreviewSeries,
} from '@/trendData/trend.actions';
import { clearOutstandingDebouncedAsyncFunctions } from '@/utilities/asyncDebounce';
import type { AnyProperty } from '@/utilities.types';
import { CONFIG_SCHEMA_VERSION } from '@/utilities/configUpgrader.utilities';
import { sqItemPropertiesStore, sqTrendMetricStore, sqWorkbenchStore } from '@/core/core.stores';
import { setDuplicatingItemId } from '@/tools/itemProperties/propertiesPanel.actions';
import { addMetricColumn, removeItemsFromTable } from '@/tableBuilder/tableBuilder.actions';
import { ProcessTypeEnum } from '@/sdk/model/ThresholdMetricOutputV1';
import { setHasCreatedCondition, setTriggerConfetti } from '@/workbench/workbench.actions';
import { isSystemTest } from '@/core/utilities';
import { broadcastSimpleMetricCreated } from '@/tools/thresholdMetric/thresholdMetric.actions';
import { addStatsSummary } from '@/summaryReport/summaryReport.actions';
import { addOrUpdateColumn } from '@/tableDefinitionEditor/tableGridColumns.utilities';
import type { ItemTableCell, ProcessedMaterializedTable } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { getMaterializedTable } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { CSV_TYPES, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { API_TYPES_TO_ITEM_TYPES, ITEM_TYPES } from '@/trendData/trendData.constants';
import { t } from 'i18next';
import { getItemsInScalingTableRow } from '@/search/search.utilities';
import type { RowIdInputListV1 } from '@/sdk/model/RowIdInputListV1';
import type { ItemOutputV1 } from '@/sdk/model/ItemOutputV1';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import type { TableComputeOutputV1 } from '@/sdk/model/TableComputeOutputV1';
import type { GraphQLOutputV1 } from '@/sdk/model/GraphQLOutputV1';
import type { DetailsPaneItemWithScalingProps, ParameterItemWithScalingProps } from './toolRunner.types';

export const panelExecuteCalculatedItem = panelExecuteFormula(createCalculatedItem);

export const panelExecuteCondition = panelExecuteFormula(createCondition());

export const panelExecuteSignal = panelExecuteFormula(createSignal());

export const panelExecuteScalar = panelExecuteFormula(createScalar());

export const panelExecuteStatisticsSummary = panelExecuteFormula(createStatisticsSummary());

/**
 }
 * Handles the logistics of what should happen when the "Execute" button is clicked on a tool panel that creates an
 * item. Store the UI Config, add the item to the trend, fetch dependencies, handle errors
 *
 * @param {Function} create - the create function is called to create a new item; should resolve with the item to be
 *   added
 * @param {Function} update - the update function is called to update an existing item
 * @param {Object} config - the UI Config to set on the item
 * @param {string} [id] - the unique id of an item if it already exists, or undefined if a new item
 * @param {string} [color] - the color the item should appear in the details pane
 * @param {boolean} [notifyOnError] - show a notification on error
 * @param {boolean} [closeOnSuccess] - close the investigate panel on success
 * @param {Object} [additionalProps] - additional properties to add to the item in the trend
 * @param {boolean} [scalingColumn] - the newly added scaling column to be added to the trend
 *
 * @returns { Promise<string> }
 */
export function panelExecute(
  create: () => Promise<any>,
  update: () => Promise<any>,
  config,
  id?: string,
  color?: string,
  additionalProps?: Record<string, unknown>,
  scalingColumn?: { id: string; itemType: ITEM_TYPES },
  { notifyOnError = true, closeOnSuccess = true } = {},
): Promise<any> {
  let promise: Promise<void> = scalingColumn ? Promise.resolve() : undefined;
  let newItem: any = scalingColumn ? scalingColumn : undefined;
  let props = additionalProps;
  const isNew = !id || !!scalingColumn;

  if (config.type === TREND_TOOLS.FFT_TABLE || config.type === TREND_TOOLS.AGGREGATION_BINS_TABLE) {
    props = _.set(props, 'calculationType', config.type);
  }

  if (!scalingColumn) {
    if (!isNew) {
      promise = update();
    } else {
      promise = create().then((item) => {
        id = item.id;
        newItem = item;
      });
    }
  }

  return (
    promise
      // Set the UI Config as soon as we know the item exists, so that we reopen the correct tool panel on error
      .then(() =>
        sqItemsApi.setProperty({ value: JSON.stringify(config) }, { id, propertyName: SeeqNames.Properties.UIConfig }),
      )
      .then(() => {
        if (newItem && !_.isEmpty(sqItemPropertiesStore.duplicatingItemId)) {
          return duplicateProperties(sqItemPropertiesStore.duplicatingItemId, id);
        }
      })
      .finally(() => {
        setDuplicatingItemId('');
      })
      .then(() => {
        if (newItem) {
          if (color) {
            newItem.color = color;
          }

          if (config.type === TREND_TOOLS.STATISTICS_SUMMARY) {
            return addStatsSummary(newItem);
          }

          return addTrendItem(newItem, props);
        } else {
          // Specifically not returning the promise so that user does not need to wait for all items to fetch before
          // closing
          fetchItemAndDependents(id);
        }
      })
      .then(() => {
        clearOutstandingDebouncedAsyncFunctions();
        cancelPreviewCapsules();
        removePreviewCapsules();
        cancelPreviewSeries();
        removePreviewSeries();

        // Recompute the table definition if an item is from a scaling table and edited from the details pane
        const item = findItemIn(getTrendStores(), id);
        if (!scalingColumn && item?.tableId && item?.columnId && item?.rowId) {
          recomputeScalingColumn(item.tableId, item.columnId, [id], false);
        }

        if (closeOnSuccess) {
          closeInvestigationTool();
        }
        if (
          !isSystemTest() &&
          !sqWorkbenchStore.hasCreatedCondition &&
          newItem?.type === SeeqNames.Types.CalculatedCondition
        ) {
          setHasCreatedCondition(true);
          setTriggerConfetti(true, 'ACCOMPLISHMENTS.CONDITION_CREATED');
        }
        return Promise.resolve(id);
      })
      .catch((e) => {
        const status = _.get(e, 'status');

        // 504 is triggered when user cancels
        if (status !== HttpCodes.GATEWAY_TIMEOUT) {
          if (status === HttpCodes.BAD_REQUEST) {
            if (e.data.statusMessage) {
              e.data.statusMessage = e.data.statusMessage.replace(/[\s\S]+(Formula error .*)/, '$1');
            } else {
              e.data.statusMessage = 'Formula error: Unknown';
            }
          }

          if (notifyOnError) {
            errorToast({ httpResponseOrError: e });
          }

          catchItemDataFailure(id, undefined, e);
        }

        if (isNew && id) {
          loadToolForEdit(id);
        } else {
          setDisplayMode(DISPLAY_MODE.EDIT, PUSH_IGNORE);
        }

        return Promise.reject(formatApiError(e));
      })
  );
}

/**
 * Creates or updates a threshold metric item and updates the state of the tool panel.
 *
 * @param {String} type - the type that the function should result in
 * @param {Object} definition - An object defining a table
 * @param {String} definition.name - Name for display in the details panel.
 * @param {String} definition.formula - The formula to be run to generate the tables.
 * @param {String[]} definition.parameters - The bound parameters to run the formula (unbound parameters are added by
 *   this function)
 * @param {Object} [config] - the UI Config to set on the item
 * @param {string} [id] - the unique id of an item if it already exists, or undefined if a new item
 * @param {string} [color] - the color the item should appear in the details pane
 * @param {boolean} [notifyOnError] - show a notification on error
 * @param {boolean} [closeOnSuccess] - close the investigate panel on success
 *
 * @returns {Promise<string>}
 */
export function panelExecuteThresholdMetric(
  definition: ThresholdMetricInputV1,
  config,
  id?: string,
  color?: string,
  { notifyOnError = true, closeOnSuccess = true } = {},
) {
  _.assign(definition, { scopedTo: getTrendItemScopedTo(id) });
  return panelExecute(
    () =>
      sqMetricsApi.createThresholdMetric(definition).then(({ data }) => {
        if (data.processType === ProcessTypeEnum.Simple) {
          broadcastSimpleMetricCreated(data.id, definition);
        }

        return data;
      }),
    async () => {
      const oldProcessType = sqTrendMetricStore.findItem(id).definition.processType;
      const isCondition = (processType) =>
        [ProcessTypeEnum.Condition, ProcessTypeEnum.Continuous].includes(processType);

      const wasCondition = isCondition(oldProcessType);
      const { data } = await sqMetricsApi.putThresholdMetric(definition, { id });
      if (wasCondition && !isCondition(data.processType)) {
        removeItemsFromTable([data]);
      } else if (!wasCondition && isCondition(data.processType)) {
        addMetricColumn(data);
      }

      return data;
    },
    config,
    id,
    color,
    undefined,
    undefined,
    { notifyOnError, closeOnSuccess },
  );
}

/**
 * Creates or updates a formula item and updates the state of the tool panel.
 *
 * @param {String} type - the type that the function should result in
 * @param {Object} definition - An object defining a table
 * @param {String} definition.name - Name for display in the details panel.
 * @param {String} definition.formula - The formula to be run to generate the tables.
 * @param {String[]} definition.parameters - The bound parameters to run the formula (unbound parameters are added by
 *   this function)
 * @param {Object} config - the UI Config to set on the item
 * @param {string} [id] - the unique id of an item if it already exists, or undefined if a new item
 * @param {string} [color] - the color the item should appear in the details pane
 * @param {boolean} [notifyOnError] - show a notification on error
 * @param {boolean} [closeOnSuccess] - close the investigate panel on success
 *
 * @returns {Promise<string>}
 */
export function panelExecuteFormulaFunction(
  type,
  definition,
  config,
  id?: string,
  color?: string,
  { notifyOnError = true, closeOnSuccess = true } = {},
) {
  _.assign(definition, { scopedTo: getTrendItemScopedTo(id) });
  return panelExecute(
    () => sqFormulasApi.createFunction({ type, ...definition }).then(({ data }) => data),
    () => sqFormulasApi.updateFunction(definition, { id }).then(({ data }) => data),
    config,
    id,
    color,
    undefined,
    undefined,
    { notifyOnError, closeOnSuccess },
  );
}

/**
 * Creates an item and updates the state of the tool panel.
 *
 * @param name - Name for calculation
 * @param formula - The formula to pass to the Calculation Engine
 * @param parameters - Map of parameter name to ID that are the top-level parameters used in the formula
 * @param configParams - the UI Config to set on the item
 * @param [id] - the unique id of an item if it already exists, or undefined if a new item
 * @param [color] - the color the item should appear in the details pane
 * @param [notifyOnError] - show a notification on error
 * @param [closeOnSuccess] - close the investigate panel on success
 * @param scalingTableId - the ID of the scaling table to which the column should be added
 * @param itemType - the type of item to create (series, condition, formula item)
 *
 * @returns {Promise<string>}
 */
export async function panelExecuteItem(
  name: string,
  formula: string,
  parameters: Record<string, unknown>,
  configParams: AnyProperty,
  scalingTableId?: string,
  id?: string,
  color?: string,
  itemType: ITEM_TYPES = ITEM_TYPES.SERIES,
  { notifyOnError = true, closeOnSuccess = true } = {},
) {
  if (scalingTableId) {
    return panelExecuteScalingColumn(name, formula, parameters, configParams, scalingTableId, id, color, {
      notifyOnError,
      closeOnSuccess,
    });
  } else if (itemType === ITEM_TYPES.CONDITION) {
    return panelExecuteCondition(name, formula, parameters, configParams, id, color, { notifyOnError, closeOnSuccess });
  } else if (itemType === ITEM_TYPES.FORMULA_ITEM) {
    return panelExecuteCalculatedItem(name, formula, parameters, configParams, id, color, {
      notifyOnError,
      closeOnSuccess,
    });
  } else {
    return panelExecuteSignal(name, formula, parameters, configParams, id, color, { notifyOnError, closeOnSuccess });
  }
}

/**
 * Creates a scaling table column and updates the state of the tool panel.
 *
 * @param name - Name for calculation
 * @param formula - The formula to pass to the Calculation Engine
 * @param parameters - Map of parameter name to ID that are the top-level parameters used in the formula
 * @param config - the UI Config to set on the item
 * @param [id] - the unique id of an item if it already exists, or undefined if a new item
 * @param [color] - the color the item should appear in the details pane
 * @param [notifyOnError] - show a notification on error
 * @param [closeOnSuccess] - close the investigate panel on success
 * @param scalingTableId - the ID of the scaling table to which the column should be added
 *
 */
export async function panelExecuteScalingColumn(
  name: string,
  formula: string,
  parameters: Record<string, unknown>,
  config: AnyProperty,
  scalingTableId: string,
  id?: string,
  color?: string,
  { notifyOnError = true, closeOnSuccess = true } = {},
) {
  const detailsPaneItems = getAllItems({});

  const paramItems = await collectAndValidateParametersForScalingColumn(
    formula,
    parameters,
    detailsPaneItems,
    scalingTableId,
    id,
  );

  const { scalingTableData, recomputeColumnId } = await createAndAddColumn(
    name,
    formula,
    paramItems,
    scalingTableId,
    detailsPaneItems,
    id,
  );

  const { processedMaterializedTable } = await recomputeAndGetProcessedTable(
    scalingTableId,
    recomputeColumnId,
    name,
    scalingTableData,
    paramItems,
    detailsPaneItems,
  );

  const { scalingProps, rowItem } = await getScalingColumnProps(
    processedMaterializedTable,
    scalingTableData,
    scalingTableId,
    recomputeColumnId,
    id,
  );

  // Trigger full column recompute in background (don't wait)
  recomputeScalingColumn(scalingTableId, recomputeColumnId, undefined, false);

  return panelExecute(
    () => Promise.resolve(),
    () => Promise.resolve(),
    config,
    rowItem.uuid,
    color,
    scalingProps,
    { id: rowItem.uuid, itemType: API_TYPES_TO_ITEM_TYPES[rowItem.fetchedProperties[SeeqNames.Properties.Type]] },
    { notifyOnError, closeOnSuccess },
  );
}

/**
 * Creates a function that creates or updates a formula item and updates the state of the tool panel.
 *
 * @param creator - function from calculationRunner utilities that will create the item
 */
export function panelExecuteFormula(creator: FormulaCreator) {
  /**
   * Creates or updates a formula item and updates the state of the tool panel.
   *
   * @param name - Name for calculation
   * @param formula - The formula to pass to the Calculation Engine
   * @param parameters - Map of parameter name to ID that are the top-level parameters used in the formula
   * @param config - the UI Config to set on the item
   * @param [id] - the unique id of an item if it already exists, or undefined if a new item
   * @param [color] - the color the item should appear in the details pane
   * @param [notifyOnError] - show a notification on error
   * @param [closeOnSuccess] - close the investigate panel on success
   */
  return (
    name: string,
    formula: string,
    parameters,
    config: AnyProperty,
    id?: string,
    color?: string,
    { notifyOnError = true, closeOnSuccess = true } = {},
  ) =>
    panelExecute(
      () => creator({ name, scopedTo: getTrendItemScopedTo(id), formula, parameters }),
      () => updateFormulaItem({ id, name, formula, parameters }),
      { ...config, configVersion: CONFIG_SCHEMA_VERSION },
      id,
      color,
      undefined,
      undefined,
      { notifyOnError, closeOnSuccess },
    );
}

const SEEQ_PROPERTIES_TO_COPY: string[] = [SeeqNames.Properties.NumberFormat];
const SEEQ_PROPERTIES_TO_SKIP: string[] = _.filter(
  SeeqNames.Properties,
  (property) => !SEEQ_PROPERTIES_TO_COPY.includes(property),
);

async function duplicateProperties(sourceItemId: string, newItemId: string) {
  const properties = await sqItemsApi.getItemAndAllProperties({ id: sourceItemId });
  const descriptionValue = removeExportDirectiveFromDescription(properties.data.description);
  const propertiesToCopy: ScalarPropertyV1[] = properties.data.properties
    .filter((property) => !SEEQ_PROPERTIES_TO_SKIP.includes(property.name))
    .map((prop: PropertyOutputV1) => ({ name: prop.name, value: prop.value, unitOfMeasure: prop.unitOfMeasure }))
    .concat(
      descriptionValue
        ? {
            name: SeeqNames.Properties.Description,
            value: descriptionValue,
            unitOfMeasure: undefined,
          }
        : [],
    );
  if (propertiesToCopy.length > 0) {
    await sqItemsApi.setProperties(propertiesToCopy, { id: newItemId });
  }
}

// Export directive regex should be in sync with
// DuplicationData.removeExportFromDescription and com.seeq.link.sdk.export.ExportDirective.parse
function removeExportDirectiveFromDescription(description) {
  if (description && description.trim().length > 0) {
    const newDescription = description.replace(/\[EXPORT TO.*/s, '');
    if (newDescription !== description) {
      return newDescription;
    }
    return description;
  }
}

function getParametersInDetailsPane(
  usedParameters: Record<string, unknown>,
  detailsPaneItems: DetailsPaneItemWithScalingProps[],
): ParameterItemWithScalingProps[] {
  return Object.entries(usedParameters)
    .map(([identifier, paramId]) => {
      const item = detailsPaneItems.find((item) => item.id === paramId && item.tableId && item.columnName);
      return item ? ({ ...item, identifier } as ParameterItemWithScalingProps) : undefined;
    })
    .filter((item): item is ParameterItemWithScalingProps => item !== undefined);
}

function getParameterIdsNotInDetailsPane(
  usedParameters: Record<string, unknown>,
  detailsPaneItems: DetailsPaneItemWithScalingProps[],
): string[] {
  return Object.entries(usedParameters)
    .filter(([, paramId]) => !detailsPaneItems.find((item) => item.id === paramId && item.tableId && item.columnName))
    .map(([, paramId]) => paramId as string);
}

function addScalingPropsToParametersNotInDetailsPane(
  parametersNotInDetailsPaneResult: ItemOutputV1[],
  usedParameters: Record<string, unknown>,
  row: { materializedTableRow: ItemTableCell[]; scalingTableData: TableDefinitionOutputV1 } | undefined,
  editingItem: DetailsPaneItemWithScalingProps | undefined,
): ParameterItemWithScalingProps[] {
  return parametersNotInDetailsPaneResult.map((parameterNotInDetailsPane): ParameterItemWithScalingProps => {
    const cellIndex = row?.materializedTableRow.findIndex((rowItem) => rowItem.uuid === parameterNotInDetailsPane.id);
    if (cellIndex !== undefined && cellIndex > -1 && row && editingItem) {
      const cell = row.materializedTableRow[cellIndex];
      const scalingTableProperties = {
        tableId: editingItem.tableId,
        rowId: editingItem.rowId,
        columnName: cell.columnName,
        columnId: row.scalingTableData.columnDefinitions[cellIndex].id,
        identifier: Object.entries(usedParameters).find(([, id]) => id === parameterNotInDetailsPane.id)?.[0],
      };
      return { ...parameterNotInDetailsPane, ...scalingTableProperties } as ParameterItemWithScalingProps;
    } else {
      return { ...parameterNotInDetailsPane } as ParameterItemWithScalingProps;
    }
  });
}

function validateAllParametersFromSameScalingTable(
  paramItems: ParameterItemWithScalingProps[],
  scalingTableId: string,
): Promise<void> {
  const areAllParametersFromSameScalingTable = _.every(
    paramItems,
    (item) => item && item.columnId && item.columnName && item.rowId && item.tableId === scalingTableId,
  );

  if (!areAllParametersFromSameScalingTable) {
    return Promise.reject(t('SCALING.ALL_ITEMS_FROM_TABLE_ERROR'));
  }
  return Promise.resolve();
}

async function getScalingTableData(scalingTableId: string): Promise<TableDefinitionOutputV1> {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: scalingTableId });
  if (!scalingTableData) {
    return Promise.reject(t('SCALING.TABLE_NOT_FOUND_ERROR'));
  }
  return scalingTableData;
}

function createFormulaColumnDefinition(
  name: string,
  formula: string,
  paramItems: ParameterItemWithScalingProps[],
  scalingTableData: TableDefinitionOutputV1,
  id?: string,
): ColumnDefinitionInputV1 {
  const formulaColumnIndexes = paramItems.map(
    (item) => scalingTableData.columnDefinitions.findIndex((col) => col.columnName === item.columnName) + 1,
  );

  return {
    columnName: name,
    columnType: ColumnTypeEnum.UUID,
    columnRules: [
      {
        formulaCreator: {
          columnIndexes: formulaColumnIndexes,
          formula,
          name,
          parameters: paramItems.map((item) => item.identifier),
          scopedTo: getTrendItemScopedTo(id),
        },
      },
    ],
    columnUom: undefined,
    isIndexed: false,
  };
}

function getItemIdColumnName(scalingTableData: TableDefinitionOutputV1): string {
  const isScalingTableCSV = CSV_TYPES.includes(scalingTableData.tableType);
  return isScalingTableCSV ? SeeqNames.MaterializedTables.CsvItemIdColumn : SeeqNames.MaterializedTables.ItemIdColumn;
}

/**
 * Recomputes a column in the table definition, either for specific rows or the entire column.
 * @param scalingTableId - The scaling table ID
 * @param recomputeColumnId - The column ID to recompute
 * @param itemIds - Optional array of item IDs to recompute specific rows. If empty or undefined, recomputes entire column.
 * @param waitForCompletion - Whether to wait for the recompute to complete (default: true)
 * @returns Promise that resolves with the recompute response if waiting, or void if not waiting
 */
async function recomputeScalingColumn(
  scalingTableId: string,
  recomputeColumnId: string,
  itemIds?: string[],
  waitForCompletion = true,
): Promise<TableComputeOutputV1 | void> {
  const rowIds: RowIdInputListV1 = {
    rowIds: itemIds?.map((itemId) => ({ itemId })) || [],
  };

  if (!waitForCompletion) {
    sqTableDefinitionsApi
      .recomputeColumnInTableDefinition(rowIds, {
        id: scalingTableId,
        columnId: recomputeColumnId,
        includeDetailedResults: true,
      })
      .catch((error) => {
        console.error('Failed to recompute column in background:', error);
      });
    return Promise.resolve();
  }

  const { data: recomputeResponse } = await sqTableDefinitionsApi.recomputeColumnInTableDefinition(rowIds, {
    id: scalingTableId,
    columnId: recomputeColumnId,
    includeDetailedResults: true,
  });

  return recomputeResponse;
}

async function getMaterializedTableForScalingColumn(
  scalingTableId: string,
  itemIdColumnName: string,
  columnName: string,
  itemId: string,
): Promise<GraphQLOutputV1> {
  const valueFilter = {
    valueFilter: {
      filterType: GraphQL.FilterTypes.Equals,
      columnName: itemIdColumnName,
      value: itemId,
    },
  };

  return await getMaterializedTable(
    scalingTableId,
    [itemIdColumnName, columnName],
    [
      {
        uuidColumn: itemIdColumnName,
        propertyNames: [SeeqNames.Properties.Name, SeeqNames.Properties.Type],
      },
      {
        uuidColumn: columnName,
        propertyNames: [
          SeeqNames.Properties.Name,
          SeeqNames.Properties.Type,
          SeeqNames.Properties.IsModifiedOutsideTable,
        ],
      },
    ],
    undefined,
    undefined,
    undefined,
    valueFilter,
  );
}

function processAndValidateMaterializedTable(
  materializedTable: GraphQLOutputV1,
  recomputeResponse: TableComputeOutputV1,
): Promise<ProcessedMaterializedTable> {
  const processedMaterializedTable = processMaterializedTable(
    materializedTable.data?.table,
    recomputeResponse.tableDefinition.columnDefinitions.map((column) => {
      return { ...column, displayName: column.columnName };
    }),
  );

  if (processedMaterializedTable.rows.length === 0) {
    return Promise.reject(t('SCALING.NO_ROWS_ERROR'));
  }

  return Promise.resolve(processedMaterializedTable);
}

async function collectAndValidateParametersForScalingColumn(
  formula: string,
  parameters: Record<string, unknown>,
  detailsPaneItems: DetailsPaneItemWithScalingProps[],
  scalingTableId: string,
  id?: string,
): Promise<ParameterItemWithScalingProps[]> {
  const paramNames = Array.from(formula.matchAll(/\$([a-zA-Z_][a-zA-Z0-9_]*)/g), (m) => m[1]);
  const usedParameters = _.pick(parameters, paramNames);

  const parametersInDetailsPane = getParametersInDetailsPane(usedParameters, detailsPaneItems);
  const parameterIdsNotInDetailsPane = getParameterIdsNotInDetailsPane(usedParameters, detailsPaneItems);
  const parametersNotInDetailsPanePromises = parameterIdsNotInDetailsPane.map((paramId) =>
    sqItemsApi.getItemAndAllProperties({ id: paramId }),
  );
  const parametersNotInDetailsPaneResults = await Promise.all(parametersNotInDetailsPanePromises);
  const parametersNotInDetailsPaneResult = parametersNotInDetailsPaneResults.map((result) => result.data);

  const editingItem = detailsPaneItems.find((item) => item.id === id);
  const row =
    editingItem && editingItem.tableId
      ? await getItemsInScalingTableRow(editingItem.tableId, editingItem.rowId)
      : undefined;

  const parametersNotInDetailsPane = addScalingPropsToParametersNotInDetailsPane(
    parametersNotInDetailsPaneResult,
    usedParameters,
    row,
    editingItem,
  );

  const paramItems = [...parametersInDetailsPane, ...parametersNotInDetailsPane];
  await validateAllParametersFromSameScalingTable(paramItems, scalingTableId);

  return paramItems;
}

async function createAndAddColumn(
  name: string,
  formula: string,
  paramItems: ParameterItemWithScalingProps[],
  scalingTableId: string,
  detailsPaneItems: DetailsPaneItemWithScalingProps[],
  id?: string,
): Promise<{
  columnResult: { tableDefinition: TableDefinitionOutputV1 };
  scalingTableData: TableDefinitionOutputV1;
  existingColumnId?: string;
  recomputeColumnId: string;
}> {
  const scalingTableData = await getScalingTableData(scalingTableId);
  const { columnId: existingColumnId } = detailsPaneItems.find((item) => item.id === id) || {};

  const formulaColumnDefinition = createFormulaColumnDefinition(name, formula, paramItems, scalingTableData, id);

  const existingColumnDefinition = scalingTableData.columnDefinitions.find((col) => col.id === existingColumnId);
  const updatedFormulaColumnDefinition = existingColumnDefinition
    ? { ...existingColumnDefinition, ...formulaColumnDefinition }
    : formulaColumnDefinition;

  const columnResult = await addOrUpdateColumn(
    EDITOR_TYPES.SCALING,
    scalingTableId,
    updatedFormulaColumnDefinition,
    existingColumnId,
    true,
  );

  if (!columnResult) {
    return Promise.reject(t('SCALING.COLUMN_ADD_ERROR'));
  }

  const recomputeColumnId = existingColumnId
    ? existingColumnId
    : columnResult.tableDefinition.columnDefinitions.find((column) => column.columnName === name)?.id;

  return { columnResult, scalingTableData, existingColumnId, recomputeColumnId };
}

async function recomputeAndGetProcessedTable(
  scalingTableId: string,
  recomputeColumnId: string,
  columnName: string,
  scalingTableData: TableDefinitionOutputV1,
  paramItems: ParameterItemWithScalingProps[],
  detailsPaneItems: DetailsPaneItemWithScalingProps[],
): Promise<{ processedMaterializedTable: ProcessedMaterializedTable }> {
  const itemIdColumnName = getItemIdColumnName(scalingTableData);
  const itemId =
    paramItems.length > 0
      ? paramItems[0].rowId
      : detailsPaneItems.find((item) => item.tableId === scalingTableId).rowId;

  // Recompute for specific row to get immediate results
  const recomputeResponse = await recomputeScalingColumn(scalingTableId, recomputeColumnId, [itemId], true);
  if (!recomputeResponse) {
    return Promise.reject(new Error('Failed to recompute column'));
  }

  const materializedTable = await getMaterializedTableForScalingColumn(
    scalingTableId,
    itemIdColumnName,
    columnName,
    itemId,
  );

  const processedMaterializedTable = await processAndValidateMaterializedTable(materializedTable, recomputeResponse);

  return { processedMaterializedTable };
}

async function getScalingColumnProps(
  processedMaterializedTable: ProcessedMaterializedTable,
  scalingTableData: TableDefinitionOutputV1,
  scalingTableId: string,
  recomputeColumnId: string,
  id?: string,
): Promise<{
  scalingProps: {
    tableName: string;
    tableId: string;
    rowName: string | undefined;
    rowId: string;
    columnName: string | undefined;
    columnId: string;
  };
  rowItem: ItemTableCell;
}> {
  const itemRowItem = processedMaterializedTable.rows[0][0] as ItemTableCell;
  const rowItem = processedMaterializedTable.rows[0][1] as ItemTableCell;

  const scalingProps = {
    tableName: scalingTableData.name,
    tableId: scalingTableId,
    rowName: itemRowItem.columnName,
    rowId: itemRowItem.uuid,
    columnName: rowItem.fetchedProperties[SeeqNames.Properties.Name],
    columnId: recomputeColumnId,
  };

  if (rowItem.fetchedProperties[SeeqNames.Properties.IsModifiedOutsideTable] && id) {
    await sqItemsApi.deleteProperty({
      id,
      propertyName: SeeqNames.Properties.IsModifiedOutsideTable,
    });
  }

  return { scalingProps, rowItem };
}
